// frontend/src/pages/SplashScreen.js
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { LightBlueButton } from '../components/buttonStyles';
import sdsmCollegeLogo from '../assets/sdsmcollege.png'; // Import the image

const SplashScreen = () => {
  const navigate = useNavigate();
  const [zoom, setZoom] = useState(false); // State to trigger zoom animation

  useEffect(() => {
    const timer = setTimeout(() => {
      setZoom(true); // Trigger zoom animation
      setTimeout(() => {
        navigate('/home'); // Navigate after zoom animation completes
      }, 1000); // Delay navigation to allow zoom animation to finish
    }, 7000); // Start zoom animation after 7 seconds (20 seconds total)
    return () => clearTimeout(timer);
  }, [navigate]);

  const handleSkip = () => {
    setZoom(true); // Trigger zoom animation
    setTimeout(() => {
      navigate('/home'); // Navigate after zoom animation completes
    }, 1000); // Delay navigation to allow zoom animation to finish
  };

  return (
    <SplashContainer>
      <ContentWrapper className={zoom ? 'zoom' : ''}>
        <AnimatedLogo>
          <CollegeName>SDSM College</CollegeName>
          <LogoContainer>
            <LogoImage src={sdsmCollegeLogo} alt="SDSM College Logo" />
            <GlowingCircle />
          </LogoContainer>
        </AnimatedLogo>
        
        <DeveloperInfo>
          <InfoText>Developed by Student Developers</InfoText>
          <InfoText>© 2024 All Rights Reserved</InfoText>
          <PartnerLink href="https://sdsmcollege.in" target="_blank" rel="noopener noreferrer">
            In collaboration with DSM College
          </PartnerLink>
        </DeveloperInfo>

        <LoadingContainer>
          <Spinner />
          <LoadingText>
            <TypewriterText>Loading School Management System...</TypewriterText>
          </LoadingText>
        </LoadingContainer>

        <SkipButton onClick={handleSkip}>
          Enter Now
        </SkipButton>
      </ContentWrapper>
    </SplashContainer>
  );
};

export default SplashScreen;

// Animations
const float = keyframes`
  0% { transform: translateY(0px); }
  50% { transform: translateY(-20px); }
  100% { transform: translateY(0px); }
`;

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const pencilDraw = keyframes`
  0% { stroke-dashoffset: 1000; }
  100% { stroke-dashoffset: 0; }
`;

const glow = keyframes`
  0% { box-shadow: 0 0 10px rgba(255, 215, 0, 0.6); }
  50% { box-shadow: 0 0 20px rgba(255, 215, 0, 0.8); }
  100% { box-shadow: 0 0 10px rgba(255, 215, 0, 0.6); }
`;

const typewriter = keyframes`
  from { width: 0; }
  to { width: 100%; }
`;

const zoomIn = keyframes`
  from { transform: scale(1); }
  to { transform: scale(5); opacity: 0; }
`;

// Styled Components
const SplashContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  background: linear-gradient(135deg, #004d40, #b8860b);
  padding: 2rem;
`;

const ContentWrapper = styled.div`
  text-align: center;
  max-width: 800px;
  transition: transform 1s ease-in-out, opacity 1s ease-in-out;

  &.zoom {
    animation: ${zoomIn} 1s ease-in-out forwards;
  }
`;

const AnimatedLogo = styled.div`
  animation: ${float} 3s ease-in-out infinite, ${fadeIn} 2s ease-in;
  margin-bottom: 2rem;
`;

const CollegeName = styled.h1`
  font-size: 2.5rem;
  color: #ffd700;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
  margin-bottom: 1rem;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    left: 0;
    bottom: -5px;
    width: 100%;
    height: 2px;
    background: #ffd700;
    animation: ${pencilDraw} 2s ease-in-out;
    stroke-dasharray: 1000;
    stroke-dashoffset: 1000;
  }
`;

const LogoContainer = styled.div`
  position: relative;
  width: 150px;
  height: 150px;
  margin: 0 auto;
`;

const LogoImage = styled.img`
  width: 100%;
  height: 100%;
  border-radius: 50%;
  object-fit: cover;
  animation: ${fadeIn} 2s ease-in;
`;

const GlowingCircle = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border-radius: 50%;
  border: 4px solid #ffd700;
  box-shadow: 0 0 10px rgba(255, 215, 0, 0.6);
  animation: ${glow} 2s ease-in-out infinite;
`;

const DeveloperInfo = styled.div`
  margin: 2rem 0;
  padding: 1.5rem;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  backdrop-filter: blur(5px);
  animation: ${fadeIn} 2s ease-in;
`;

const InfoText = styled.p`
  color: #fff;
  font-size: 1.1rem;
  margin: 0.5rem 0;
`;

const PartnerLink = styled.a`
  color: #ffd700;
  text-decoration: none;
  font-weight: 500;
  display: block;
  margin-top: 1rem;
  transition: all 0.3s ease;

  &:hover {
    text-decoration: underline;
    transform: scale(1.05);
  }
`;

const LoadingContainer = styled.div`
  margin: 2rem 0;
  animation: ${fadeIn} 2s ease-in;
`;

const Spinner = styled.div`
  width: 50px;
  height: 50px;
  border: 4px solid #fff;
  border-top: 4px solid #ffd700;
  border-radius: 50%;
  animation: ${spin} 1s linear infinite;
  margin: 0 auto;
`;

const LoadingText = styled.p`
  color: #fff;
  margin-top: 1rem;
  font-size: 1.2rem;
`;

const TypewriterText = styled.span`
  display: inline-block;
  overflow: hidden;
  white-space: nowrap;
  animation: ${typewriter} 3s steps(40, end);
`;

const SkipButton = styled(LightBlueButton)`
  background: linear-gradient(135deg, #ffd700, #b8860b);
  color: #004d40;
  font-weight: 600;
  padding: 12px 30px;
  border-radius: 25px;
  transition: all 0.3s ease;
  font-size: 1.1rem;
  animation: ${fadeIn} 2s ease-in;

  &:hover {
    transform: scale(1.05);
    box-shadow: 0 4px 15px rgba(255, 215, 0, 0.4);
  }
`;